import { getEdgeWorkerUrl } from '../../lib/edgeWorkerUrl';
import { sanitizePayload } from '../../lib/sanitize';
import React, { useState, useEffect } from 'react';
import { FiCpu, FiClock, FiCheckCircle, FiMail, FiAlertTriangle } from 'react-icons/fi';
import { supabase } from '../../lib/supabaseClient';

export default function OnyxInvestigationPanel({ ticketId }) {
  const [telemetry, setTelemetry] = useState(null);
  const [flightRecorder, setFlightRecorder] = useState(null);
  const [nodeId, setNodeId] = useState(null);
  const [isRoboticsTicket, setIsRoboticsTicket] = useState(false);
  const [lastBriefedAt, setLastBriefedAt] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDegraded, setIsDegraded] = useState(false);

  const fetchInvestigationData = async () => {
    if (!ticketId) return;
    setIsLoading(true);
    try {
      const { data: telemetryData } = await supabase
        .from('ticket_ai_telemetry')
        .select('*')
        .eq('ticket_id', ticketId)
        .maybeSingle();

      setTelemetry(telemetryData || null);

      if (telemetryData && telemetryData.synthetic) {
          setIsDegraded(true);
      } else {
          setIsDegraded(false);
      }

      // Phase 161: `/api/v1/robotics/fault-ingest` stores the flight recorder on
      // the ticket itself (`support_tickets.metadata.flight_recorder_data`), and
      // this panel used to render hard-coded "DIVERGENCE DETECTED" strings while
      // never reading the payload. Operators were reading fiction on urgent
      // hardware tickets. The real payload is loaded here.
      const { data: ticketRow } = await supabase
        .from('support_tickets')
        .select('metadata')
        .eq('id', ticketId)
        .maybeSingle();

      const metadata = ticketRow?.metadata || null;
      setFlightRecorder(metadata?.flight_recorder_data || null);
      setNodeId(metadata?.node_id || null);
      setIsRoboticsTicket(metadata?.is_robotics === true);

      const { data: briefingEvent } = await supabase
        .from('events_ax2024')
        .select('timestamp')
        .eq('type', 'thread_executive_briefing_exported')
        .eq('payload->>ticket_id', ticketId)
        .order('timestamp', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (briefingEvent?.timestamp) {
        setLastBriefedAt(new Date(briefingEvent.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      } else {
        setLastBriefedAt(null);
      }
    } catch (err) {
      console.error('Failed to load investigation telemetry:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvestigationData();

    // Listen for live briefing export events from WebSocket channel
    const handleLiveBriefing = (e) => {
      if (e.detail?.payload?.ticket_id === ticketId) {
        setLastBriefedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      }
    };

    window.addEventListener('axim:briefing_exported', handleLiveBriefing);
    return () => window.removeEventListener('axim:briefing_exported', handleLiveBriefing);
  }, [ticketId]);

  if (isLoading) {
    return (
      <div className="p-5 rounded-3xl bg-zinc-950/60 border border-slate-800/60 font-mono text-xs text-zinc-500 animate-pulse flex flex-col gap-3 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-zinc-800 animate-pulse"></div>
          <div className="h-3 w-48 bg-zinc-800 rounded animate-pulse"></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="h-16 rounded-xl bg-zinc-900/50 animate-pulse"></div>
          <div className="h-16 rounded-xl bg-zinc-900/50 animate-pulse"></div>
          <div className="h-16 rounded-xl bg-zinc-900/50 animate-pulse"></div>
          <div className="h-16 rounded-xl bg-zinc-900/50 animate-pulse"></div>
        </div>
      </div>
    );
  }

  const edgeHeartbeats = telemetry?.EDGE_HEARTBEAT_INTERCEPTS || 0;
  const d1Timeouts = telemetry?.D1_TIMEOUT_COUNT || 0;
  const showDegradedSecondary = edgeHeartbeats > 0 || d1Timeouts > 0;

  const isRobotics =
    telemetry?.category === 'Robotics / Hardware Fault' || isRoboticsTicket || !!flightRecorder;

  // The fleet gateway decides the flight-recorder schema, so the HUD lists the
  // scalar readings it actually received instead of guessing field names.
  const flightRecorderReadings =
    flightRecorder && typeof flightRecorder === 'object' && !Array.isArray(flightRecorder)
      ? Object.entries(flightRecorder).filter(([, value]) =>
          ['string', 'number', 'boolean'].includes(typeof value)
        )
      : [];

  return (
    <div className="p-5 rounded-3xl bg-zinc-950/60 border border-slate-800/60 backdrop-blur-md space-y-4 font-mono">
      <div className="flex items-center justify-between border-b border-zinc-900 pb-3">
        <div className="flex items-center gap-2 text-indigo-400 text-xs font-bold">
          <FiCpu className="text-sm animate-pulse"/>
          <span className="uppercase tracking-wider">Onyx Edge Neural Investigation</span>
          {isDegraded && (
              <span className="ml-2 text-[10px] text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20 flex items-center gap-1 uppercase">
                  <FiAlertTriangle className="text-[10px]" /> [Degraded (Edge-Cached)]
              </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {showDegradedSecondary && (
               <span className="text-[9px] text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded border border-orange-500/20 flex items-center gap-1 font-bold">
                  {d1Timeouts > 0 ? '[DB: DEGRADED]' : '[Sessions: EDGE-CACHED]'}
               </span>
          )}
          {lastBriefedAt && (
            <span className="text-[9px] text-sky-300 bg-sky-500/10 px-2 py-0.5 rounded border border-sky-500/20 flex items-center gap-1">
              <FiMail className="text-[9px]"/> Briefed: {lastBriefedAt}
            </span>
          )}

          {telemetry?.is_curated && (
            <span className="text-[9px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20 flex items-center gap-1">
              <FiCheckCircle className="text-[9px]"/> Curated Analysis
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <div className="p-3 rounded-xl bg-black/40 border border-zinc-900">
          <span className="text-[10px] text-zinc-500 uppercase font-bold block mb-0.5">Sentiment</span>
          <span className={`font-bold ${
            telemetry?.sentiment === 'positive' ? 'text-emerald-400' :
            telemetry?.sentiment === 'negative' ? 'text-rose-400' : 'text-amber-400'
          }`}>
            {telemetry?.sentiment || 'Neutral'}
          </span>
        </div>

        <div className="p-3 rounded-xl bg-black/40 border border-zinc-900">
          <span className="text-[10px] text-zinc-500 uppercase font-bold block mb-0.5">Category</span>
          <span className="text-zinc-200 font-bold uppercase">{telemetry?.category ? sanitizePayload({ text: telemetry.category }).text : 'General'}</span>
        </div>

        <div className="p-3 rounded-xl bg-black/40 border border-zinc-900">
          <span className="text-[10px] text-zinc-500 uppercase font-bold block mb-0.5">Confidence</span>
          <span className="text-indigo-400 font-bold">{telemetry?.confidence ? `${telemetry.confidence}%` : '85%'} </span>
        </div>

        <div className="p-3 rounded-xl bg-black/40 border border-zinc-900">
          <span className="text-[10px] text-zinc-500 uppercase font-bold block mb-0.5">Triage Latency</span>
          <span className="text-emerald-400 font-bold flex items-center gap-1">
            <FiClock className="text-[10px]"/> {telemetry?.generation_latency_ms || '42'}ms
          </span>
        </div>
      </div>

      {isRobotics && (
        <div className="mt-4 p-3 rounded-xl bg-red-900/20 border border-red-500/30">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 text-red-400 font-bold text-xs uppercase">
              <FiAlertTriangle /> Robotics Flight Recorder HUD
            </div>
            {nodeId && (
              <span className="text-[10px] text-zinc-400">
                node: <span className="text-zinc-200 font-bold">{sanitizePayload({ text: String(nodeId) }).text}</span>
              </span>
            )}
          </div>

          {flightRecorderReadings.length === 0 ? (
            <div className="text-[10px] text-amber-400/90 font-mono leading-relaxed">
              No flight recorder payload ingested for this ticket.
              <span className="block text-zinc-500">
                Waiting on `POST /api/v1/robotics/fault-ingest` or the fleet gateway relay.
              </span>
            </div>
          ) : (
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1 text-[10px] font-mono">
              {flightRecorderReadings.map(([key, value]) => (
                <div key={key} className="flex items-baseline justify-between gap-2 border-b border-red-500/10 py-0.5">
                  <dt className="text-zinc-500 uppercase truncate">{sanitizePayload({ text: String(key) }).text}</dt>
                  <dd className="text-zinc-200 font-bold text-right">
                    {sanitizePayload({ text: String(value) }).text}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}
    </div>
  );
}
